import {Modal, ModalBody, ModalContent, ModalFooter, ModalHeader} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useEffect, useRef, useState} from "react";
import services from "../../services";
import {PASS_OFFER, passOfferOn} from "../../pricing/paymentsV3";
import {useGlobalModal} from "../../core/GlobalModal.tsx";
import {GO_LIVE_GATE, ONE_OFF_PASS_BENEFITS, PASS_FOLLOWUP} from "../../pricing/plans.ts";
import PButton from "../PButton.tsx";

/** The go-live gate's payload (OneOffPathService::gatePayload with `gate: 'golive'`). */
export interface GoLiveGatePayload {
    code: string;
    gate: 'golive';
    /** What the modal sells: the sized Event Pass, and nothing beside it. */
    offer?: 'event_pass';
    message?: string;
    tournament_uid?: string | null;
    league_uid?: string | null;
    event_type?: 'tournament' | 'league';
    entries: number;
    unit: 'teams' | 'players';
    cap: number;
    amount_cents: number;
    list_cents?: number;
    paid_cents?: number;
    currency: string;
    entries_covered?: number | null;
    band_label?: string;
    esports?: boolean;
    /** An unused pass in the organizer's wallet (earned, granted, bought ahead): "Use your pass" leads. */
    held_pass?: boolean;
    /** What that held pass would still owe on this field (a first-tier pass on a bigger event). */
    held_pass_topup_cents?: number;
    held_pass_tier?: number | null;
    held_pass_label?: string | null;
    event_tier?: number;
    /** A pass already on the event that covers less: the modal is the top-up, not a new pass. */
    attached_pass_tier?: number | null;
    club_uid?: string | null;
    draw_preview?: {home: string; away: string}[];
    /** The free results before the pass: at the third, the title says they have seen it work. */
    free_scores?: {allowed: number; used: number; left: number} | null;
}

interface GoLivePassModalProps {
    data: GoLiveGatePayload;
    tournamentUid?: string;
    leagueUid?: string;
    returnPath?: string;
    onUnlocked?: () => void;
}

export const isGoLiveGate = (x: unknown): x is GoLiveGatePayload =>
    !!x && typeof x === 'object' && (x as {gate?: string}).gate === 'golive';

const fmt = (cents: number, currency: string) => {
    try {
        return new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(cents / 100);
    } catch {
        return `${currency.toUpperCase()} ${(cents / 100).toFixed(2)}`;
    }
};

/**
 * The gate's one offer, at the third result: "You've seen it work — the pass runs the rest."
 * Round one of the REAL draw sits behind the price, so the organizer sees their own event rather
 * than a feature list, and the sized Event Pass is the single route with one primary button. No
 * plans, no trial, no comparison here: Plus and its trial are offered where Plus features are.
 * Checkout opens in this window and the event unlocks on the return (OneOffPassBanner confirms
 * the session; the webhook does the same if it lands first).
 */
export default function GoLivePassModal({data, tournamentUid, leagueUid, returnPath, onUnlocked}: GoLivePassModalProps) {
    const globalModal = useGlobalModal();
    const tUid = tournamentUid ?? data.tournament_uid ?? undefined;
    const lUid = tUid ? undefined : (leagueUid ?? data.league_uid ?? undefined);
    const [busy, setBusy] = useState<null | 'buy' | 'use'>(null);
    const heldPass = !!data.held_pass;
    // A pass is already on the event (covering less): the only thing left is the top-up itself.
    const attached = data.attached_pass_tier != null && data.code === 'event_pass_topup_required';
    const topup = Math.max(0, Number(attached ? data.amount_cents : (data.held_pass_topup_cents ?? 0)));
    const usePassLabel = attached
        ? `${GO_LIVE_GATE.resumeTopup} — ${fmt(topup, data.currency || 'usd')}`
        : topup > 0 ? `${GO_LIVE_GATE.usePass} + ${fmt(topup, data.currency || 'usd')} top-up` : GO_LIVE_GATE.usePass;

    // A pass already held goes on the event here and now; the server stamps and logs it. A held
    // pass from a lower tier is topped up: it goes on, then checkout opens for the difference.
    const useHeldPass = async () => {
        setBusy('use');
        void track('plan', attached ? 'topup_resumed' : 'used_held_pass');
        try {
            // Attached already: straight to the top-up checkout (the server prices the difference).
            if (!attached) await services.eventPass.apply({tournament_uid: tUid, league_uid: lUid, surface: 'modal'});
            if (topup > 0) {
                const {redirect_url} = await services.eventPass.checkout(
                    returnPath ?? (window.location.pathname + window.location.search), undefined, 'sized', lUid, tUid,
                );
                window.location.href = redirect_url;
                return;
            }
            addToast({color: 'success', title: GO_LIVE_GATE.usePassDone});
            globalModal.close();
            onUnlocked?.();
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not apply the pass.'});
            setBusy(null);
        }
    };
    // "Have a code?" (pass_offer_enabled): the one-time offer's code, checked by the server against
    // this club's offer and THIS price. Valid, the buy button charges the discounted amount (the
    // code goes to Stripe as the discount); used, expired or foreign, the box says so and the
    // full price stands.
    const [code, setCode] = useState('');
    const [offer, setOffer] = useState<{code: string; valid: boolean; reason: string | null; message: string | null; percent: number; discounted_cents: number | null} | null>(null);
    const [checking, setChecking] = useState(false);
    const applyCode = async () => {
        const c = code.trim().toUpperCase();
        if (!c) return;
        setChecking(true);
        try {
            const r: any = await services.eventPass.status({tournament_uid: tUid, league_uid: lUid, offer_code: c} as any);
            setOffer(r?.offer ?? null);
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not check the code.'});
        } finally {
            setChecking(false);
        }
    };
    const offerCode = offer?.valid ? offer.code : undefined;
    const price = fmt(offerCode && offer?.discounted_cents != null ? offer.discounted_cents : data.amount_cents, data.currency || 'usd');
    const unit = data.entries === 1 ? data.unit.slice(0, -1) : data.unit;
    const preview = (data.draw_preview ?? []).slice(0, 8);
    // Arrived from the follow-up email (?src=pass_followup_email): the checkout carries the source for
    // attribution and the guarantee the email made is repeated under the offer. Nothing is recorded here.
    const src = (() => { try { return new URLSearchParams(window.location.search).get('src') || undefined; } catch { return undefined; } })();
    const fromFollowupEmail = src === PASS_FOLLOWUP.src;

    // Master → Activation reads which route was taken: the offer-click beacon as before, plus a
    // gate_impressions row with the action. Measurements only — a route never waits on them.
    const track = (action: 'shown' | 'buy' | 'plan', taken?: string): Promise<unknown> => {
        if (!(tUid || lUid)) return Promise.resolve();
        const sent = Promise.all([
            services.eventPass.offerClick({tournament_uid: tUid, league_uid: lUid, action, amount_cents: data.amount_cents}).catch(() => null),
            services.eventPass.gateImpression({
                gate: 'go_live', code: data.code, surface: 'modal', tournament_uid: tUid, league_uid: lUid,
                entries: data.entries, unit: data.unit, offer_shown: 'event_pass', action_taken: taken ?? action,
            }).catch(() => null),
        ]);
        return Promise.race([sent, new Promise((resolve) => setTimeout(resolve, 500))]);
    };
    useEffect(() => { void track('shown'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Closed without taking a route (the X, Escape, the backdrop, Later): the "dismissed" half of the
    // funnel, which report:gate-outcomes reads. Once per opening, and never after a buy or held
    // pass was chosen. A measurement only: the modal closes whatever the beacon does.
    const dismissed = useRef(false);
    const dismiss = () => {
        if (!dismissed.current && busy === null && (tUid || lUid)) {
            dismissed.current = true;
            services.eventPass.gateImpression({
                gate: 'go_live', code: data.code, surface: 'modal', tournament_uid: tUid, league_uid: lUid,
                entries: data.entries, unit: data.unit, offer_shown: 'event_pass', action_taken: 'dismissed',
            }).catch(() => null);
        }
        globalModal.close();
    };

    const buyPass = async () => {
        setBusy('buy');
        void track('buy');
        try {
            const {redirect_url} = await services.eventPass.checkout(
                returnPath ?? (window.location.pathname + window.location.search),
                undefined,
                'sized',
                lUid,
                tUid,
                undefined,
                offerCode,
                src,
            );
            window.location.href = redirect_url;
        } catch (e: any) {
            addToast({color: 'danger', title: e?.message || e?.error || 'Could not start checkout.'});
            setBusy(null);
        }
    };

    return (
        <Modal isOpen onClose={dismiss} placement="center" scrollBehavior="inside" classNames={{wrapper: 'z-[130]', backdrop: 'z-[125]'}}>
            <ModalContent>
                <ModalHeader className="flex-col items-start gap-1">
                    <span className="flex items-center gap-2">
                        <Icon icon="material-symbols-light:lock-outline" className="text-2xl text-primary" />
                        {GO_LIVE_GATE.modalTitle(data.entries, unit, data.cap, data.free_scores)}
                    </span>
                    <span className="text-xs font-normal text-default-500">{GO_LIVE_GATE.sub(data.cap, data.event_type === 'league')}</span>
                </ModalHeader>
                <ModalBody className="gap-3">
                    <p className="text-sm text-default-600">{GO_LIVE_GATE.body}</p>

                    {/* Round one of THEIR draw: real names, read from the fixtures the server built. */}
                    {preview.length > 0 && (
                        <div className="rounded-lg border border-default-200 bg-content1 p-3">
                            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-default-500">{GO_LIVE_GATE.previewTitle}</div>
                            <ul className="grid gap-1 text-xs sm:grid-cols-2">
                                {preview.map((g, i) => (
                                    <li key={i} className="flex min-w-0 items-center gap-1.5 rounded border border-default-100 px-2 py-1">
                                        <span className="truncate font-medium">{g.home}</span>
                                        <span className="shrink-0 text-default-400">v</span>
                                        <span className="truncate font-medium">{g.away}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* The one offer: the pass, sized for THIS event, priced by the server's resolver. */}
                    <div className="rounded-lg border-2 border-dashed border-[#E6C200] bg-[#FFFCEC] p-3" data-testid="event-pass-offer">
                        <div className="flex items-start gap-3">
                            <Icon icon="material-symbols-light:confirmation-number-outline" className="shrink-0 text-2xl text-[#9a7d00]" />
                            <div className="min-w-0 flex-1 text-sm">
                                <div className="flex flex-wrap items-center gap-2 font-semibold">
                                    {GO_LIVE_GATE.passName}{data.band_label ? ` — ${data.band_label}` : ''}
                                    <span className="text-[#9a7d00]">{price} one-time</span>
                                </div>
                                <div className="mt-0.5 text-xs text-default-500">
                                    {GO_LIVE_GATE.passWhat(data.event_type === 'league')}
                                    {data.entries_covered != null ? ` Covers up to ${data.entries_covered} ${data.unit}.` : ` Covers any number of ${data.unit}.`}
                                </div>
                                <ul className="mt-2 space-y-0.5 text-xs text-default-600">
                                    {ONE_OFF_PASS_BENEFITS.benefitsFor(!!data.esports).map((b) => (
                                        <li key={b} className="flex items-start gap-1.5">
                                            <Icon icon="material-symbols-light:check-circle-outline" className="mt-0.5 shrink-0 text-base text-success" />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* The follow-up email's promise, repeated where the buy happens. */}
                    {fromFollowupEmail && (
                        <div className="rounded-lg border border-success-200 bg-success-50/50 dark:bg-success-900/10 px-3 py-2 text-xs text-default-600" data-testid="pass-guarantee">
                            <span className="font-semibold text-foreground">{PASS_FOLLOWUP.guaranteeTitle}</span> {PASS_FOLLOWUP.guaranteeBody}
                        </div>
                    )}

                    {/* "Have a code?": the one-time offer's code, while the offer is on. */}
                    {passOfferOn() && !attached && (
                        <div className="rounded-lg border border-default-200 bg-content1 px-3 py-2 text-xs" data-pass-offer-box>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold">{PASS_OFFER.haveCode}</span>
                                <input value={code} onChange={(e) => { setCode(e.target.value); setOffer(null); }} placeholder={PASS_OFFER.placeholder} maxLength={12}
                                       aria-label={PASS_OFFER.haveCode}
                                       className="w-32 rounded-md border border-default-200 bg-content1 px-2 py-1 font-mono text-xs uppercase outline-none focus:border-primary" />
                                <PButton size="sm" variant="flat" onPress={applyCode} isLoading={checking} isDisabled={!code.trim() || busy !== null}>{PASS_OFFER.apply}</PButton>
                            </div>
                            {offer && (
                                <div className={'mt-1 ' + (offer.valid ? 'font-semibold text-success' : 'text-danger')} data-pass-offer-result>
                                    {offer.valid
                                        ? PASS_OFFER.applied(offer.percent, fmt(offer.discounted_cents ?? data.amount_cents, data.currency || 'usd'), fmt(data.amount_cents, data.currency || 'usd'))
                                        : offer.message}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Which held pass "Use your pass" puts on, when there is one. */}
                    {(heldPass || attached) && (
                        <div className="rounded-lg border border-success-200 bg-success-50/50 dark:bg-success-900/10 px-3 py-2 text-xs text-default-600">
                            {attached
                                ? GO_LIVE_GATE.attachedTopupBody
                                : <>{GO_LIVE_GATE.usePassWhich(data.held_pass_label || 'held')}{topup > 0 ? ` This event is ${data.entries} ${data.unit}, so ${fmt(topup, data.currency || 'usd')} more is due when you go live.` : ' It covers this event in full.'}</>}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter className="flex-wrap items-center gap-2">
                    <PButton variant="light" onPress={dismiss}>{GO_LIVE_GATE.later}</PButton>
                    {/* ONE primary button: the pass. A held pass leads instead, and buying steps back. */}
                    {!attached && (
                        <PButton color="primary" variant={heldPass ? 'flat' : 'solid'} onPress={buyPass} isLoading={busy === 'buy'} isDisabled={busy === 'use'}
                                 startContent={<Icon icon="material-symbols-light:confirmation-number-outline" className="text-lg" />}>
                            {`${GO_LIVE_GATE.buy} — ${price}`}
                        </PButton>
                    )}
                    {(heldPass || attached) && (
                        <PButton color="primary" onPress={useHeldPass} isLoading={busy === 'use'} isDisabled={busy === 'buy'}
                                 startContent={<Icon icon="material-symbols-light:lock-open-outline" className="text-lg" />}>
                            {usePassLabel}
                        </PButton>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
